import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../../../components/AdminLayout";
import "./index.css";
import { Button, Tag, Modal, Table, Dropdown, Alert, message } from "antd";
import { useState, useEffect, useMemo, useCallback } from "react";
import { ContentDetailTableKey, emptyContentDetailData } from "../utils/contentDetailTypes";
import DetailPage from "../../Landing/DetailPage";
import type { ColumnTypes, DetailItem } from "../utils/editableTable";
import EditableCell, { EditableRow } from "../utils/editableTable";
import { CloseOutlined, ExclamationCircleOutlined, EyeOutlined, MoreOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useContentDetail } from "../ContentManagement/hooks/useContentDetail";
import contentService, { type ContentCreateRequest } from "../../../services/contentService";
import { useContentManagement } from "../ContentManagement/hooks/useContentManagement";
import { formatThaiDate } from "../../../utils/dateFormatter";
import { useUserInfo } from "../../../hooks/useUserInfo";
import UserSearchSelect from "../../../components/UserSearchSelect";
import UserDisplay from "../../../components/UserDisplay";

const DxDetail: React.FC = () => {

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // API integration
  const {
    contentDetail,
    metadata,
    isLoading,
    isSaving,
    error,
    loadContent,
    saveContent,
    deleteContent,
    updateContentDetail,
  } = useContentDetail(id);

  const {
    fetchContents,
  } = useContentManagement();

  // Local state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState<"" | "TH" | "EN">("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [isVersionView, setIsVersionView] = useState(false); // Track if viewing a specific version

  // User info hook
  const { userInfo } = useUserInfo();

  // Determine user role and permissions
  const userRole = useMemo(() => {
    if (!userInfo?.roles || userInfo.roles.length === 0) return null;
    
    if (userInfo.roles.includes('super-admin')) return 'super-admin';
    if (userInfo.roles.includes('admin')) return 'admin';
    if (userInfo.roles.includes('content-owner')) return 'content-owner';
    if (userInfo.roles.includes('content-member')) return 'content-member';
    
    return null;
  }, [userInfo]);

  const userEmail = userInfo?.email;

  // Check if user can delete content (only super-admin)
  const canDeleteContent = userRole === 'super-admin';

  // Check if user can modify content owner (only super-admin)
  const canModifyOwner = userRole === 'super-admin';

  // Check if user can modify content members
  // super-admin, admin, or users who are actually the content owner
  const canModifyMembers = useMemo(() => {
    if (userRole === 'super-admin' || userRole === 'admin') return true;
    if (!metadata || !userEmail) return false;
    return metadata.content_owner === userEmail;
  }, [userRole, metadata, userEmail]);

  // Check if user can edit (all except users who are only content-member without being owner)
  const canEdit = useMemo(() => {
    if (userRole === 'super-admin' || userRole === 'admin') return true;
    if (!metadata || !userEmail) return false;
    // Can edit if user is the content owner
    return metadata.content_owner === userEmail;
  }, [userRole, metadata, userEmail]);

  // Check if user has access to view/edit this content
  const hasContentAccess = useCallback(() => {
    // super-admin and admin can access all content
    if (userRole === 'super-admin' || userRole === 'admin') return true;
    
    if (!metadata || !userEmail) return false;
    
    // content-owner or content-member can access content they own or are members of
    if (userRole === 'content-owner' || userRole === 'content-member') {
      const isOwner = metadata.content_owner === userEmail;
      const isMember = metadata.content_members?.includes(userEmail);
      
      return isOwner || isMember;
    }
    
    return false;
  }, [metadata, userEmail, userRole]);

  const queryParams = new URLSearchParams(window.location.search);
  const mode = queryParams.get('mode');
  const version = queryParams.get('version');
  const isEditMode = (id === "new" || mode === 'edit') && canEdit && !isVersionView; // Only allow edit mode if user can edit and not viewing version

  // Load content when component mounts or id changes
  useEffect(() => {
    if (id && id !== "new") {
      if (version) {
        // Load specific version
        loadContent(id, version);
        setIsVersionView(true);
      } else {
        // Load latest editable version
        loadContent(id);
        setIsVersionView(false);
      }
    } else if (id === "new") {
      // Initialize with empty data for new content
      const initialData = {
        ...emptyContentDetailData,
        permission: {
          ...emptyContentDetailData.permission,
          owner: userInfo?.email || '', // Set current user's email as owner
        }
      };
      updateContentDetail(initialData);
      setIsVersionView(false);
    }
  }, [id, version, userInfo?.email]);

  // Check access permissions after content is loaded
  useEffect(() => {
    // Skip access check for new content
    if (id === "new") return;
    
    // Wait until metadata and userInfo are loaded
    if (!metadata || !userInfo) return;
    
    // Check if user has access
    if (!hasContentAccess()) {
      navigate('/error');
    }
  }, [metadata, userInfo, hasContentAccess, navigate, id]);

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string; })[] = [
    {
      title: 'หัวข้อ',
      dataIndex: 'name',
      width: '30%',
      render: (text: any, record: DetailItem) => {
        // If it's a header row, return the original content
        if (typeof record.key === 'string' && record.key.includes('-header-')) {
          return text;
        }

        // For regular rows, add red asterisk if required
        if (record.required && isEditMode) {
          return (
            <span>
              <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
              {text}
            </span>
          );
        }

        return text;
      },
      onCell: (record: DetailItem) => {
        if (typeof record.key === 'string' && record.key.includes('-header-')) {
          return { colSpan: 2 };
        }
        return {};
      },
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'value',
      editable: isEditMode,
      onCell: (record: DetailItem) => {
        if (typeof record.key === 'string' && record.key.includes('-header-')) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
  ];

  const statusTag = (status: string) => {
    let color = '';
    let text = '';

    if (status === 'published') {
      color = 'green';
      text = 'เผยแพร่แล้ว';
    } else if (status === 'draft') {
      color = 'orange';
      text = 'แบบร่าง';
    }

    return <Tag color={color}>{text}</Tag>;
  }

  // In your components object:
  const components = {
    body: {
      row: EditableRow,
      cell: (props: any) => <EditableCell {...props} hasValidationError={hasFieldError(props.record?.key)} />,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DetailItem) => ({
        record,
        editable: col.editable && !(metadata?.key && record.key === `${ContentDetailTableKey.Overview}-dxNo`),
        dataIndex: col.dataIndex,
        title: col.title,
        colSpan: col.onCell ? col.onCell(record)?.colSpan : 1,
        handleSave: handleSave,
      }),
    };
  });

  const handleSave = (row: DetailItem) => {
    if (!contentDetail) return;

    // Find which table this row belongs to by its key
    const key = row.key as string;
    let updated = { ...contentDetail };

    // Overview
    if (key.startsWith(`${ContentDetailTableKey.Overview}-`)) {
      const field = key.split('-')[1];
      updated[ContentDetailTableKey.Overview] = {
        ...updated[ContentDetailTableKey.Overview],
        [field]: row.value,
      };
    }

    // Permission
    if (key.startsWith(`${ContentDetailTableKey.Permission}-`)) {
      const field = key.endsWith('owner') ? 'owner' : 'member';
      if (field === 'owner') {
        updated[ContentDetailTableKey.Permission] = {
          ...updated[ContentDetailTableKey.Permission],
          owner: String(row.value || ''),
        };
      }
      // Note: member field is handled separately with custom components
    }

    updateContentDetail(updated);
  }

  // Helper function to check if a field has validation error
  const hasFieldError = (fieldKey: string): boolean => {
    return validationErrors.includes(fieldKey);
  };

  // Validation function to check required fields
  const validateRequiredFields = (): { isValid: boolean; errors: string[]; fieldErrors: Record<string, string> } => {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    if (!contentDetail) {
      errors.push('ไม่มีข้อมูลคอนเทนต์');
      return { isValid: false, errors, fieldErrors };
    }

    const isEmpty = (value: string | undefined | null): boolean => {
      return !value || value.trim() === '' || value.trim() === '-';
    };

    // Overview validation
    const overview = contentDetail[ContentDetailTableKey.Overview];
    if (isEmpty(overview.dxNo)) {
      errors.push('DX Module Code กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-dxNo`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.titleTh)) {
      errors.push('Module Title (TH) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-titleTh`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.titleEn)) {
      errors.push('Module Title (EN) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-titleEn`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.shortDescTh)) {
      errors.push('Summary Description (TH) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-shortDescTh`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.shortDescEn)) {
      errors.push('Summary Description (EN) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-shortDescEn`] = 'กรุณากรอกข้อมูลให้ครบ';
    }

    // Permission validation
    const permission = contentDetail[ContentDetailTableKey.Permission];
    if (isEmpty(permission.owner)) {
      errors.push('Content Owner กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Permission}-owner`] = 'กรุณากรอกข้อมูลให้ครบ';
    }

    return { isValid: errors.length === 0, errors, fieldErrors };
  };

  // Save function for content creation/update
  const handleSaveContent = async () => {
    const validation = validateRequiredFields();
    if (!validation.isValid) {
      setValidationErrors(Object.keys(validation.fieldErrors));
      return;
    }
    else {
      setValidationErrors([]);
    }

    if (id === "new") {
      try {
        const content: ContentCreateRequest = {
          dx_number: contentDetail?.overview.dxNo || '',
          title_th: contentDetail?.overview.titleTh || '',
          title_en: contentDetail?.overview.titleEn || '',
          short_description_th: contentDetail?.overview.shortDescTh || '',
          short_description_en: contentDetail?.overview.shortDescEn || '',
          status: 'draft',
          content_members: contentDetail?.permission.member || [],
        };

        // Only include content_owner if user is super-admin
        if (userRole === 'super-admin') {
          content.content_owner = contentDetail?.permission.owner || '';
        }

        // validate dx_number uniqueness
        const existingContent = await fetchContents();
        if (existingContent != undefined) {
          const duplicate = existingContent.find(c => c.key === content.dx_number);
          if (duplicate) {
            messageApi.error(`DX Module Code "${content.dx_number}" มีอยู่ในระบบแล้ว กรุณาใช้รหัสอื่น`);
            return;
          }
        }

        const response = await contentService.createContent(content);
        if (response && response.content && response.content.SK) {
          messageApi.success('สร้างคอนเทนต์ใหม่สำเร็จ');
          navigate(`/admin/dx-list/${response.content.SK}`);
        } else {
          throw new Error('No SK returned from create content');
        }
      } catch (error) {
        console.error('Error creating new content:', error);
        messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    }
    else {
      try {
        // Granular permission control based on user role
        const success = await saveContent({
          owner: userRole === 'super-admin',
          members: canModifyMembers
        });
        if (success) {
          messageApi.success('แก้ไขสำเร็จ');
        }
      } catch (error) {
        messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
        console.error('Save error:', error);
      }
    }
  };

  // Cancel function to go back to content list
  const handleCancel = () => {
    navigate('/admin/content-list');
  };

  // Show loading state
  if (isLoading) {
    // return (
    //   <AdminLayout>
    //     <div style={{ padding: '20px', textAlign: 'center' }}>
    //       <Spin size="large" />
    //       <div style={{ marginTop: '16px' }}>Loading content...</div>
    //     </div>
    //   </AdminLayout>
    // );
  }

  // Show error state
  if (error) {
    return (
      <AdminLayout>
        <div style={{ padding: '20px' }}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={() => navigate('/admin/content-list')}>
                Back to List
              </Button>
            }
          />
        </div>
      </AdminLayout>
    );
  }

  // Show not found for invalid content
  if (id !== "new" && !contentDetail) {
    return (
      <AdminLayout>
        <div style={{ padding: '20px' }}>
          <Alert
            message="Content Not Found"
            description="The requested content could not be found."
            type="warning"
            showIcon
            action={
              <Button type="primary" onClick={() => navigate('/admin/content-list')}>
                Back to List
              </Button>
            }
          />
        </div>
      </AdminLayout>
    );
  }

  const overviewTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const overview = contentDetail[ContentDetailTableKey.Overview] || {};
    const dataSource: DetailItem[] = [
      {
        key: `${ContentDetailTableKey.Overview}-dxNo`,
        name: 'DX Module Code',
        value: overview.dxNo,
        required: true,
        maxLength: 10,
      },
      {
        key: `${ContentDetailTableKey.Overview}-titleTh`,
        name: 'Module Title (TH)',
        value: overview.titleTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Overview}-titleEn`,
        name: 'Module Title (EN)',
        value: overview.titleEn,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Overview}-shortDescTh`,
        name: 'Summary Description (TH)',
        value: overview.shortDescTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Overview}-shortDescEn`,
        name: 'Summary Description (EN)',
        value: overview.shortDescEn,
        required: true,
        maxLength: 200,
      },
    ];
    return renderTable("รายละเอียดเบื้องต้น", dataSource, false);
  }

  // Helper functions for permission management
  const handleOwnerChange = (email: string) => {
    if (!contentDetail) return;
    
    const updated = {
      ...contentDetail,
      [ContentDetailTableKey.Permission]: {
        ...contentDetail[ContentDetailTableKey.Permission],
        owner: email,
      },
    };
    updateContentDetail(updated);
  };

  const handleMemberChange = (index: number, email: string) => {
    if (!contentDetail) return;
    
    const newMembers = [...contentDetail[ContentDetailTableKey.Permission].member];
    newMembers[index] = email;
    
    const updated = {
      ...contentDetail,
      [ContentDetailTableKey.Permission]: {
        ...contentDetail[ContentDetailTableKey.Permission],
        member: newMembers,
      },
    };
    updateContentDetail(updated);
  };

  const addMember = () => {
    if (!contentDetail) return;
    
    const updated = {
      ...contentDetail,
      [ContentDetailTableKey.Permission]: {
        ...contentDetail[ContentDetailTableKey.Permission],
        member: [...contentDetail[ContentDetailTableKey.Permission].member, ''],
      },
    };
    updateContentDetail(updated);
  };

  const removeMember = (index: number) => {
    if (!contentDetail) return;
    
    const newMembers = contentDetail[ContentDetailTableKey.Permission].member.filter((_, i) => i !== index);
    // Ensure at least one empty member field remains
    if (newMembers.length === 0) {
      newMembers.push('');
    }
    
    const updated = {
      ...contentDetail,
      [ContentDetailTableKey.Permission]: {
        ...contentDetail[ContentDetailTableKey.Permission],
        member: newMembers,
      },
    };
    updateContentDetail(updated);
  };

  const PermissionTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const permission = contentDetail[ContentDetailTableKey.Permission];
    
    const permissionColumns = [
      {
        title: 'หัวข้อ',
        dataIndex: 'name',
        width: '30%',
        render: (text: string, record: { required?: boolean }) => {
          if (record.required && isEditMode) {
            return (
              <span>
                <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
                {text}
              </span>
            );
          }
          return text;
        },
      },
      {
        title: 'รายละเอียด',
        dataIndex: 'value',
      },
    ];

    const permissionDataSource = [
      // Content Owner
      {
        key: `${ContentDetailTableKey.Permission}-owner`,
        name: 'Content Owner',
        value: isEditMode && canModifyOwner ? (
          <UserSearchSelect
            value={permission.owner}
            onChange={handleOwnerChange}
            placeholder="กรอกชื่อหรืออีเมลของ Content Owner"
            style={{ 
              border: hasFieldError(`${ContentDetailTableKey.Permission}-owner`) ? '1px solid #ff4d4f' : undefined 
            }}
          />
        ) : (
          permission.owner ? <UserDisplay email={permission.owner} /> : ''
        ),
        required: true,
      },
      // Content Member Emails
      ...permission.member.map((memberEmail, index) => ({
        key: `${ContentDetailTableKey.Permission}-member-${index}`,
        name: index === 0 ? 'Content Member' : '',
        value: isEditMode && canModifyMembers ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <UserSearchSelect
              value={memberEmail}
              onChange={(email) => handleMemberChange(index, email)}
              placeholder="กรอกชื่อหรืออีเมลของ Content Member"
              style={{ flex: 1 }}
            />
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeMember(index)}
              disabled={permission.member.length === 1}
              title={permission.member.length === 1 ? "ต้องมีอย่างน้อย 1 ช่องว่างเป็น default" : "ลบ Member"}
            />
          </div>
        ) : (
          memberEmail ? <UserDisplay email={memberEmail} /> : ''
        ),
        required: false,
      }))
    ];

    const canAddMore = (contentDetail?.permission.member.length || 0) < 5;

    return (
      <div className="table-container">
        <div className="table-header">
          <h3>Content Permission</h3>
          {isEditMode && canModifyMembers && (
            <Button
              type="default"
              icon={<PlusOutlined />}
              disabled={!canAddMore}
              onClick={() => addMember()}
            >
              เพิ่ม Member ({contentDetail?.permission.member.length || 0}/5)
            </Button>
          )}
        </div>

        <Table
          bordered
          dataSource={permissionDataSource}
          columns={permissionColumns}
          pagination={false}
        />
      </div>
    );
  };

  const renderTable = (
    title: string,
    dataSource: DetailItem[],
    hasListFunction: boolean = false,
    tableType?: string
  ) => {
    const canAddMore = hasListFunction && tableType && tableType === 'member' ? (contentDetail?.permission.member.length || 0) < 5 : false;

    return (
      <div className="table-container">
        <div className="table-header">
          <h3>{title}</h3>
          {hasListFunction && isEditMode && canModifyMembers && tableType === 'member' && (
            <Button
              type="default"
              icon={<PlusOutlined />}
              disabled={!canAddMore}
              onClick={() => addMember()}
            >
              เพิ่ม Member ({contentDetail?.permission.member.length || 0}/5)
            </Button>
          )}
        </div>

        <Table<DetailItem>
          components={components}
          rowClassName={(record) => {
            if (record.key.includes('list') || record.key.includes('header')) {
              return 'header-row';
            }
            return 'editable-row';
          }}
          bordered
          dataSource={dataSource}
          columns={columns as ColumnTypes}
          pagination={false}
        />
      </div>
    );
  }

  return (
    <AdminLayout children={
      <>
        {contextHolder}
        <div>
          <div className="header-container">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, marginBottom: 16 }}>
              <h3>{id === "new" ? "แบบร่าง" : metadata?.title || "Loading..."}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                {id !== "new" && metadata && (
                  <>
                    <span style={{ color: '#888', fontSize: 14 }}>
                      อัปเดตล่าสุด {metadata.updatedAt ? formatThaiDate(metadata.updatedAt) : ""}
                    </span>
                    <div>
                        {metadata.status && 
                          (isVersionView ? (
                            statusTag(metadata.status)
                          ) : (
                            statusTag(metadata.edit_status || 'draft')
                          ))}
                        {((isVersionView && metadata.status === 'published') || (!isVersionView && metadata.edit_status === 'published')) && (
                        <span style={{ color: '#888', fontSize: 14 }}>
                          เวอร์ชัน {metadata.version}
                        </span>
                        )}
                    </div>
                  </>
                )}
              </div>
            </div>
            {(
              <div style={{ display: 'flex', gap: 12 }}>
                {!isEditMode && canEdit && (
                  <Button
                    type="default"
                    style={{ minWidth: 90 }}
                    onClick={() => { navigate(`/admin/dx-list/${id}?mode=edit`) }}
                  >
                    แก้ไข
                  </Button>
                )}
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'preview-th',
                        label: 'ดูตัวอย่างเนื้อหา (TH)',
                        icon: <EyeOutlined />,
                        style: { color: "rgba(250, 140, 22, 1)" },
                        onClick: () => setDetailVisible("TH"),
                      },
                      {
                        key: 'preview-en',
                        label: 'ดูตัวอย่างเนื้อหา (EN)',
                        icon: <EyeOutlined />,
                        style: { color: "rgba(250, 140, 22, 1)" },
                        onClick: () => setDetailVisible("EN"),
                      },
                      ...(id !== "new" && canDeleteContent
                        ? [{
                          key: 'delete',
                          label: 'ลบเนื้อหา',
                          icon: <CloseOutlined />,
                          onClick: () => setDeleteModalVisible(true),
                        }]
                        : []),
                    ],
                  }}
                  trigger={['click']}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </div>
            )}
          </div>
          
          <div className="content-container">
            {overviewTable()}
            {PermissionTable()}
          </div>

          {isEditMode && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button
                size="large"
                onClick={handleCancel}
              >
                ยกเลิก
              </Button>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  size="large"
                  type="primary"
                  onClick={() => handleSaveContent()}
                  loading={isSaving}
                  disabled={id === "new" && userRole !== 'super-admin'}
                  title={id === "new" && userRole !== 'super-admin' ? "ไม่มีสิทธิ์ในการสร้าง DX ใหม่" : undefined}
                >
                  บันทึก
                </Button>
              </div>
            </div>
          )}

          <Modal
            className="detail-modal"
            open={detailVisible !== ""}
            width="100vw"
            style={{ top: 0, maxWidth: '100%' }}
            footer={null}
            closeIcon={null}
          >
            <DetailPage data={contentDetail || undefined} onBack={() => setDetailVisible("")}  languageProp={detailVisible === "" ? undefined : detailVisible} overrideId={metadata?.key} />
          </Modal>
          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16 }}>
                <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                <span>ยืนยันการลบรายการ</span>
              </div>
            }
            open={deleteModalVisible}
            onOk={async () => {
              if (!id || id === "new") {
                setDeleteModalVisible(false);
                return;
              }

              try {
                const success = await deleteContent(id);
                if (success) {
                  setDeleteModalVisible(false);
                  navigate('/admin/content-list');
                  messageApi.success('ลบรายการสำเร็จ');
                }
              } catch (error) {
                console.error('Delete error:', error);
                messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
              }
            }}
            onCancel={() => setDeleteModalVisible(false)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
            confirmLoading={isSaving}
            centered
          >
            <p>คุณต้องการลบรายการ {metadata?.key}: {metadata?.title} หรือไม่?</p>
          </Modal>
        </div>
      </>
    } breadcrumb={{
      items: [
        { title: "ตั้งค่า" },
        { title: id === "new" ? "เพิ่มหัวข้อ (DX)" : "จัดการ DX" },
        { title: id === "new" ? "หัวข้อใหม่" : metadata?.title || "" }
      ],
    }} />
  );
};

export default DxDetail;